/**
 * The dashboard shell: navigates by URL and derives the current section's
 * selection from the path, so a reload/bookmark restores it. Nav items are
 * FILTERED by the session's permissions (routing/routes.ts's NAV_ITEMS) -- a
 * user only ever sees sections they can open.
 *
 * Two layouts share one NavList + SessionFooter (E2.1 spec section 8: phones
 * are a primary access mode, not an afterthought):
 *   - md and up: the fixed 220px left rail (unchanged from the desktop-only
 *     original -- brand block, nav list, theme toggle + email/logout/version
 *     footer).
 *   - below md: the rail disappears entirely. A slim top app bar (brand
 *     block, theme toggle, hamburger) opens a temporary Drawer holding the
 *     same permission-filtered nav list + the FX display toggle + the
 *     email/logout/version footer; picking a nav item closes the drawer.
 * The breakpoint check runs through useMediaQuery so exactly one layout is
 * mounted at a time (no duplicate nav buttons/theme-toggle in the DOM).
 */
import MenuIcon from '@mui/icons-material/Menu';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import GroupIcon from '@mui/icons-material/Group';
import KeyIcon from '@mui/icons-material/Key';
import LightModeIcon from '@mui/icons-material/LightMode';
import PersonIcon from '@mui/icons-material/Person';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SettingsIcon from '@mui/icons-material/Settings';
import ShieldIcon from '@mui/icons-material/Shield';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import { useColorScheme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import brandLogo from '../assets/brand-logo.webp';
import { useAuth } from '../auth/AuthContext.tsx';
import { APP_TITLE, APP_VERSION } from '../config.ts';
import { NATIVE_CURRENCY, useDisplayCurrency } from '../currency/CurrencyContext.tsx';
import { CurrencyToggle } from '../currency/CurrencyToggle.tsx';
import { NAV_ITEMS, ROUTES, type NavItemDef } from '../routing/routes.ts';
import { palette } from '../theme.ts';

const ICONS: Record<string, React.JSX.Element> = {
  [ROUTES.dashboard]: <QueryStatsIcon fontSize="small" />,
  [ROUTES.reports]: <AssessmentIcon fontSize="small" />,
  [ROUTES.shops]: <StorefrontIcon fontSize="small" />,
  [ROUTES.owners]: <PersonIcon fontSize="small" />,
  [ROUTES.users]: <GroupIcon fontSize="small" />,
  [ROUTES.roles]: <ShieldIcon fontSize="small" />,
  [ROUTES.config]: <SettingsIcon fontSize="small" />,
  [ROUTES.potd]: <KeyIcon fontSize="small" />,
};

function NavItem({
  active,
  label,
  icon,
  onClick,
}: Readonly<{ active: boolean; label: string; icon: React.JSX.Element; onClick: () => void }>): React.JSX.Element {
  return (
    <Box
      component="button"
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        width: '100%',
        pl: 1.25,
        pr: 1.5,
        py: 1.1,
        cursor: 'pointer',
        textAlign: 'left',
        borderTop: 'none',
        borderRight: 'none',
        borderBottom: 'none',
        borderLeft: '3px solid',
        borderLeftColor: active ? 'primary.main' : 'transparent',
        borderRadius: 1,
        color: active ? 'primary.main' : palette.textSecondary,
        bgcolor: active ? 'action.selected' : 'transparent',
        font: 'inherit',
        fontWeight: active ? 800 : 600,
        '&:hover': { bgcolor: active ? 'action.selected' : palette.quietHover },
      }}
    >
      {icon}
      <Typography component="span" sx={{ fontWeight: 'inherit', fontSize: '0.92rem' }}>
        {label}
      </Typography>
    </Box>
  );
}

/**
 * The permission-filtered nav item list -- the ONE implementation the rail
 * (md+) and the mobile Drawer both render, so their nav never drifts apart.
 */
function NavList({
  items,
  pathname,
  onNavigate,
}: Readonly<{ items: readonly NavItemDef[]; pathname: string; onNavigate: (path: string) => void }>): React.JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
      {items.map((item) => (
        <NavItem
          key={item.path}
          active={pathname === item.path || pathname.startsWith(`${item.path}/`)}
          label={item.label}
          icon={ICONS[item.path] ?? <AssessmentIcon fontSize="small" />}
          onClick={() => onNavigate(item.path)}
        />
      ))}
    </Box>
  );
}

/**
 * `labeled` renders the icon + text control for the desktop rail footer; the
 * default (icon-only) is for the mobile AppBar.
 */
function ThemeToggle({ labeled = false }: Readonly<{ labeled?: boolean }>): React.JSX.Element {
  const { mode, systemMode, setMode } = useColorScheme();
  const resolvedMode = mode === 'system' ? systemMode : mode;
  const isDark = resolvedMode === 'dark';
  const label = isDark ? 'Switch to light theme' : 'Switch to dark theme';
  const icon = isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />;
  const toggle = (): void => setMode(isDark ? 'light' : 'dark');

  if (labeled) {
    return (
      <Button
        size="small"
        variant="text"
        aria-label={label}
        onClick={toggle}
        startIcon={icon}
        sx={{
          justifyContent: 'flex-start',
          color: palette.textSecondary,
          fontWeight: 600,
          fontSize: '0.85rem',
          '&:hover': { bgcolor: palette.quietHover },
        }}
      >
        {isDark ? 'Light theme' : 'Dark theme'}
      </Button>
    );
  }

  return (
    <IconButton size="small" aria-label={label} onClick={toggle} sx={{ color: palette.textSecondary }}>
      {icon}
    </IconButton>
  );
}

/**
 * The email + log-out + app-version block shared by the rail footer and the
 * mobile Drawer footer. Deliberately carries no border/padding of its own --
 * each layout wraps it in its own footer Box (desktop's also holds the
 * ThemeToggle above it) so the single divider/padding stays that container's
 * call.
 */
function SessionFooter(): React.JSX.Element {
  const { state, logout } = useAuth();
  const displayName = state.displayName?.trim() ? state.displayName : null;
  return (
    <>
      {displayName ? (
        <Box>
          <Typography
            sx={{ display: 'block', color: 'text.primary', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}
          >
            {displayName}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: palette.textSecondary }}>
            {state.email}
          </Typography>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ display: 'block', color: palette.textSecondary }}>
          {state.email}
        </Typography>
      )}
      <Button
        size="small"
        variant="outlined"
        fullWidth
        onClick={() => void logout()}
        sx={{
          color: palette.textSecondary,
          borderColor: palette.quietBorder,
          '&:hover': { borderColor: palette.quietBorderHover, bgcolor: palette.quietHover },
        }}
      >
        Log out
      </Button>
      <Typography variant="caption" sx={{ display: 'block', color: palette.textSecondary }}>
        v{APP_VERSION}
      </Typography>
    </>
  );
}

function BrandBlock({ compact }: Readonly<{ compact?: boolean }>): React.JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: compact ? 'row' : 'column', alignItems: 'center', gap: compact ? 1 : 0 }}>
      <Box component="img" src={brandLogo} alt="1 Namba Lotto" sx={{ height: compact ? 28 : 44, maxWidth: '100%', objectFit: 'contain' }} />
      <Typography sx={{ mt: compact ? 0 : 0.5, fontWeight: 800, fontSize: compact ? '0.85rem' : '0.78rem', letterSpacing: 0.8, textAlign: compact ? 'inherit' : 'center' }}>
        {APP_TITLE}
      </Typography>
    </Box>
  );
}

export function DashboardShell(): React.JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { state } = useAuth();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const visibleItems = NAV_ITEMS.filter(
    (item) => item.permission === null || state.permissions.includes(item.permission),
  );
  // FX display conversion is internal-staff only (owner ruling 2026-08-12) --
  // External sees native PGK; presentational, not a data gate.
  const canConvertCurrency = state.permissions.includes('reports:write');
  const { displayCurrency, setDisplayCurrency } = useDisplayCurrency();
  // displayCurrency persists in localStorage, so a non-writer arriving with a
  // stored 'AUD' would be stuck on it with the toggle gone.
  useEffect(() => {
    if (!canConvertCurrency && displayCurrency !== NATIVE_CURRENCY) setDisplayCurrency(NATIVE_CURRENCY);
  }, [canConvertCurrency, displayCurrency, setDisplayCurrency]);

  function goTo(path: string): void {
    navigate(path);
    setDrawerOpen(false);
  }

  // dvh, not vh (both layouts): mobile browser chrome makes 100vh taller than
  // the visible viewport, putting the bottom of the shell out of reach.
  if (isNarrow) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', bgcolor: 'background.default', color: 'text.primary' }}>
        <AppBar
          position="static"
          elevation={0}
          sx={{ bgcolor: 'background.paper', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <BrandBlock compact />
            </Box>
            <ThemeToggle />
            <IconButton
              aria-label="Open navigation"
              onClick={() => setDrawerOpen(true)}
              sx={{ color: palette.textSecondary }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* dvh on the paper too: it is position:fixed, so its 100% default means 100vh and hides the footer behind mobile browser chrome. */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          slotProps={{ paper: { sx: { height: '100dvh' } } }}
        >
          <Box
            component="nav"
            aria-label="Dashboard sections"
            sx={{ width: 260, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, p: 1.5, bgcolor: 'background.paper' }}
          >
            <NavList items={visibleItems} pathname={pathname} onNavigate={goTo} />
            <Box sx={{ borderTop: `1px solid ${palette.quietBorder}`, pt: 1.5, px: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Relocated out of the app bar: it is the ONLY PGK<->AUD display switch, so it moves rather than vanishing at phone width. */}
              {canConvertCurrency && <CurrencyToggle labeled />}
              <SessionFooter />
            </Box>
          </Box>
        </Drawer>

        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 2 }}>
          <Outlet />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100dvh', bgcolor: 'background.default', color: 'text.primary' }}>
      <Box
        component="nav"
        aria-label="Dashboard sections"
        sx={{
          width: 220,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          p: 1.5,
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ px: 1, mb: 1.5 }}>
          <BrandBlock />
        </Box>

        <NavList items={visibleItems} pathname={pathname} onNavigate={goTo} />

        <Box sx={{ borderTop: `1px solid ${palette.quietBorder}`, pt: 1.5, px: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {canConvertCurrency && <CurrencyToggle labeled />}
          <ThemeToggle labeled />
          <SessionFooter />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 3 }}>
        <Outlet />
      </Box>
    </Box>
  );
}

export default DashboardShell;
